import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabaseClient';
import './AuthModal.css';

/**
 * SignInModal - Authentication modal with email detection
 * Supports both email and username login with password
 */
function SignInModal({ isOpen, onClose, onSuccess, onSwitchToSignUp }) {
  const [emailOrUsername, setEmailOrUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [error, setError] = useState('');
  const [userExists, setUserExists] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [forgotPasswordMode, setForgotPasswordMode] = useState(false);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setEmailOrUsername('');
      setPassword('');
      setError('');
      setUserExists(null);
      setShowPassword(false);
      setForgotPasswordMode(false);
    }
  }, [isOpen]);

  // Check if email exists when user leaves the email field
  const checkEmailExists = async (identifier) => {
    if (!identifier || identifier.length < 3) {
      setUserExists(null);
      return;
    }

    setCheckingEmail(true);
    setError('');

    try {
      // Call RPC function to check if user exists
      const { data, error: checkError } = await supabase
        .rpc('check_user_exists', {
          p_identifier: identifier.trim().toLowerCase()
        });

      if (checkError) {
        console.error('Error checking user:', checkError);
        // Don't show error to user for security reasons
        setUserExists(null);
      } else {
        setUserExists(data?.exists || false);
      }
    } catch (err) {
      console.error('Error checking email:', err);
      setUserExists(null);
    } finally {
      setCheckingEmail(false);
    }
  };

  // Handle sign in with password
  const handleSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const loginIdentifier = emailOrUsername.trim().toLowerCase();
      let email = loginIdentifier;

      // Check if it's a username (no @ symbol)
      if (!loginIdentifier.includes('@')) {
        // Look up email by username
        const { data: userData, error: lookupError } = await supabase
          .rpc('get_email_by_username', {
            p_username: loginIdentifier
          });

        if (lookupError || !userData) {
          throw new Error('Username not found');
        }
        email = userData;
      }

      // Sign in with password
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        // Track failed login attempt
        await supabase.rpc('track_failed_login', {
          p_email: email
        });
        throw error;
      }

      // Update last login timestamp
      await supabase.rpc('update_last_login', {
        p_user_id: data.user.id
      });

      onSuccess(data.user);
    } catch (err) {
      console.error('Sign in error:', err);
      if (err.message.includes('Invalid login credentials')) {
        setError('Incorrect email/username or password');
      } else if (err.message.includes('locked')) {
        setError('Account is temporarily locked. Please try again later or reset your password.');
      } else {
        setError(err.message || 'Failed to sign in. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle forgot password
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const identifier = emailOrUsername.trim().toLowerCase();
      let email = identifier;

      // If it's a username, look up the email
      if (!identifier.includes('@')) {
        const { data: userData, error: lookupError } = await supabase
          .rpc('get_email_by_username', {
            p_username: identifier
          });

        if (lookupError || !userData) {
          throw new Error('Username not found');
        }
        email = userData;
      }

      // Send password reset email
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`
      });

      if (error) throw error;

      setError(''); // Clear any errors
      alert('Password reset email sent! Please check your inbox.');
      setForgotPasswordMode(false);
    } catch (err) {
      console.error('Password reset error:', err);
      setError(err.message || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="auth-modal-overlay" onClick={onClose}>
      <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
        <button className="auth-modal-close" onClick={onClose}>×</button>

        <h2 className="auth-modal-title">
          {forgotPasswordMode ? 'Reset Password' : 'Sign In'}
        </h2>

        {forgotPasswordMode ? (
          // Forgot Password Form
          <form onSubmit={handleForgotPassword}>
            <div className="auth-form-group">
              <label>Email or Username</label>
              <input
                type="text"
                value={emailOrUsername}
                onChange={(e) => setEmailOrUsername(e.target.value)}
                placeholder="Enter your email or username"
                required
                disabled={loading}
              />
            </div>

            {error && <div className="auth-error">{error}</div>}

            <button
              type="submit"
              className="auth-submit-button"
              disabled={loading || !emailOrUsername.trim()}
            >
              {loading ? 'Sending...' : 'Send Reset Email'}
            </button>

            <div className="auth-form-footer">
              <button
                type="button"
                className="auth-link-button"
                onClick={() => setForgotPasswordMode(false)}
              >
                Back to Sign In
              </button>
            </div>
          </form>
        ) : (
          // Sign In Form
          <form onSubmit={handleSignIn}>
            <div className="auth-form-group">
              <label>Email or Username</label>
              <input
                type="text"
                value={emailOrUsername}
                onChange={(e) => setEmailOrUsername(e.target.value)}
                onBlur={(e) => checkEmailExists(e.target.value)}
                placeholder="Enter your email or username"
                required
                disabled={loading}
              />
              {checkingEmail && (
                <span className="auth-checking">Checking...</span>
              )}
            </div>

            {/* Show password field if user exists, or if they've typed something */}
            {(userExists === true || emailOrUsername.length > 0) && (
              <div className="auth-form-group">
                <label>Password</label>
                <div className="auth-password-input">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    required={userExists === true}
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="auth-password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
                {userExists === true && (
                  <button
                    type="button"
                    className="auth-link-button auth-forgot-password"
                    onClick={() => setForgotPasswordMode(true)}
                  >
                    Forgot Password?
                  </button>
                )}
              </div>
            )}

            {/* Show register prompt for new users */}
            {userExists === false && (
              <div className="auth-new-user-prompt">
                <p>New to our platform?</p>
                <button
                  type="button"
                  className="auth-register-button"
                  onClick={onSwitchToSignUp}
                >
                  Register
                </button>
              </div>
            )}

            {error && <div className="auth-error">{error}</div>}

            {/* Only show sign in button if user exists */}
            {userExists === true && (
              <button
                type="submit"
                className="auth-submit-button"
                disabled={loading || !password}
              >
                {loading ? 'Signing In...' : 'Sign In'}
              </button>
            )}
          </form>
        )}
      </div>
    </div>
  );
}

export default SignInModal;